import { Link } from "react-router-dom";
import { useDocumentMeta } from "../useDocumentMeta";
import { trackEvent } from "../../tracking/utils";

export interface FAQ {
  question: string;
  answer: string;
}

export interface CityProduct {
  title: string;
  description: string;
  url: string;
}

interface Props {
  city: string;
  title: string;
  description: string;
  path: string;
  faqs?: FAQ[];
  products?: CityProduct[];
  children: React.ReactNode;
}

const SITE_URL = "https://www.hebiktegenwind.nl";

export function CityPage({
  city,
  title,
  description,
  path,
  faqs,
  products,
  children,
}: Props) {
  useDocumentMeta({
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    canonical: `${SITE_URL}${path}`,
  });

  const faqSchema =
    faqs && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }
      : null;

  const handleProductClick = (productTitle: string) => {
    trackEvent("Add Link Clicked", {
      item: productTitle,
      provider: "bol",
      source: `city-page-${city.toLowerCase()}`,
    });
  };

  return (
    <>
      <h2>Fietsweer in {city}</h2>
      {children}

      {faqs && faqs.length > 0 ? (
        <section>
          <h3>Veelgestelde vragen over fietsweer in {city}</h3>
          {faqs.map((faq, i) => (
            <div key={i}>
              <h4>{faq.question}</h4>
              <p>{faq.answer}</p>
            </div>
          ))}
        </section>
      ) : null}

      {products && products.length > 0 ? (
        <section>
          <h3>Handige fietsspullen voor {city}</h3>
          <ul>
            {products.map((product, i) => (
              <li key={i}>
                <a
                  href={product.url}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                  onClick={() => handleProductClick(product.title)}
                >
                  <strong>{product.title}</strong>
                </a>
                {" — "}
                {product.description}
              </li>
            ))}
          </ul>
          <p style={{ fontSize: "0.85em", opacity: 0.7, marginTop: 12 }}>
            Deze pagina bevat affiliate-links via het bol Partner Programma.
            Als je iets koopt via deze links ontvangen we een kleine
            vergoeding, zonder extra kosten voor jou.
          </p>
        </section>
      ) : null}

      <p>
        <Link to="/">← Terug naar de fietsweer-tool</Link>
      </p>

      {faqSchema ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      ) : null}
    </>
  );
}
